// Campanita de notificaciones: contador de no leídas + panel con historial.
// Refresca por polling (45s) y al recibir un push en primer plano (evento ds-notification).
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getNotifications,
  markAllRead,
  markRead,
  type AppNotification,
} from '@/services/notifications'

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.round(diff / 60000)
  if (m < 1) return 'ahora'
  if (m < 60) return `hace ${m} min`
  const h = Math.round(m / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.round(h / 24)
  return `hace ${d} d`
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  })

  // Refresca cuando llega un push mientras la app está abierta.
  useEffect(() => {
    const onPush = () => qc.invalidateQueries({ queryKey: ['notifications'] })
    window.addEventListener('ds-notification', onPush)
    return () => window.removeEventListener('ds-notification', onPush)
  }, [qc])

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const items = data?.items ?? []
  const unread = data?.unread ?? 0

  const openItem = async (n: AppNotification) => {
    setOpen(false)
    if (!n.is_read) {
      try {
        await markRead(n.id)
      } catch {
        /* no bloquea la navegación */
      }
      qc.invalidateQueries({ queryKey: ['notifications'] })
    }
    if (n.url) navigate(n.url)
  }

  const onMarkAll = async () => {
    await markAllRead()
    qc.invalidateQueries({ queryKey: ['notifications'] })
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-gray-600 transition hover:bg-gray-100"
        aria-label="Notificaciones"
      >
        <span className="text-lg">🔔</span>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Móvil: panel fijo a lo ancho de la pantalla (anclado a la campana se
          salía por la izquierda y se cortaba). Escritorio: pegado a la campana. */}
      {open && (
        <div className="fixed inset-x-3 top-16 z-40 animate-fade-in overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-cardhover sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-gray-800">Notificaciones</span>
            {unread > 0 && (
              <button onClick={onMarkAll} className="text-xs font-medium text-ds-600 hover:underline">
                Marcar todas leídas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">Sin notificaciones.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-3 border-b border-gray-50 px-4 py-3 text-left transition hover:bg-gray-50 ${
                    n.is_read ? '' : 'bg-ds-50/50'
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      n.is_read ? 'bg-transparent' : 'bg-ds-500'
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-800">{n.title}</span>
                    <span className="block truncate text-xs text-gray-500">{n.body}</span>
                    <span className="mt-0.5 block text-[11px] text-gray-400">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
